<script lang="ts" setup>
import { useAuthStore } from '~/stores/auth'
import type { LoginParams } from '~/types/auth'
import { z } from 'zod'
import { useTypedForm } from '~/composables/useTypedForm'
import { requiredError } from '~/utils/zodUtils'
import { rootSvgImages } from '~/models/images'
import { pushEventToDataLayer } from '~/utils/registration'

type LoginComponentEmits = {
  login: []
}

interface LoginComponentProps {
  title?: string
  buttonText?: string
}

const { t } = useI18n('components.organisms.auth.login-component')

const emits = defineEmits<LoginComponentEmits>()
const props = withDefaults(defineProps<LoginComponentProps>(), {
  title: 'components.organisms.auth.login-component.title',
  buttonText: 'components.organisms.auth.login-component.button'
})

const authStore = useAuthStore()

const incorrectLogin = ref(false)

const { handleSubmit, formProps, isLoading } = useTypedForm<LoginParams>({
  id: 'login',
  schema: {
    email: z.email(t('error-messages.invalid-email')),
    password: z.string(requiredError(t('error-messages.password-is-required'))),
    rememberMe: z.boolean()
  },
  initialValues: {
    rememberMe: false
  }
})

const loginAsUser = handleSubmit(async (values) => {
  incorrectLogin.value = false

  const result = await authStore.login(values)

  if (!result) {
    incorrectLogin.value = true
  } else {
    pushEventToDataLayer('inloggen')
    emits('login')
  }
})
</script>

<template>
  <VCard class="rounded-2xl">
    <template #content>
      <div class="flex justify-center">
        <VImage
          :image="rootSvgImages['thuisgekookt-succes']"
          :dark="rootSvgImages['thuisgekookt-succes-dark']"
          use-img
          width="100px"
          height="100px"
        />
      </div>
      <h1 class="title text-title">
        {{ t(title) }}
      </h1>

      <VField v-if="incorrectLogin" color="danger">
        <p class="text-2xl text-danger">
          {{ t('.error-message') }}
        </p>
      </VField>

      <FormWrapper
        v-if="formProps"
        v-slot="{ k }"
        v-bind="formProps"
        @submit="loginAsUser"
      >
        <div class="flex flex-col gap-4">
          <FormInput
            :name="k.email"
            :disabled="isLoading"
            label="labels.email"
            autocomplete="email"
            type="email"
            input-mode="email"
          />

          <FormPassword
            :name="k.password"
            :disabled="isLoading"
            label="labels.password"
            autocomplete="current-password"
          />

          <FormCheckbox :ui="{ field: 'mb-6' }" :name="k.rememberMe" value="remember-me">
            {{ t('.remember-me-label') }}
          </FormCheckbox>

          <VButton
            v-if="buttonText"
            type="submit"
            :form="formProps.id"
            :loading="isLoading"
            width="full"
          >
            {{ t(buttonText) }}
          </VButton>
          <RequestPasswordResetModal />
        </div>
      </FormWrapper>
    </template>
  </VCard>
</template>

<style scoped></style>
